import React from "react";
import { X, ExternalLink, Download } from "lucide-react";
import { BACKEND_URL } from "../../config/api";

interface FactureViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  factureUrl: string;
  factureNumero: string;
}

export const FactureViewerModal: React.FC<FactureViewerModalProps> = ({
  isOpen,
  onClose,
  factureUrl,
  factureNumero,
}) => {
  if (!isOpen) return null;

  // Add base URL if it's a relative path from the backend
  const fullUrl = factureUrl
    ? factureUrl.startsWith("http")
      ? factureUrl
      : `${BACKEND_URL}${factureUrl}`
    : "";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
            <div className="flex items-center">
              <h3 className="text-lg font-medium text-gray-900" id="modal-title">
                Visualisation Facture: {factureNumero}
              </h3>
            </div>
            <div className="flex items-center space-x-2">
              {factureUrl && (
                <a 
                  href={fullUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                  title="Ouvrir dans un nouvel onglet"
                >
                  <ExternalLink className="h-5 w-5" />
                </a>
              )}
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                onClick={onClose}
              >
                <span className="sr-only">Fermer</span>
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body - Iframe */}
          <div className="flex-1 bg-gray-100 overflow-hidden relative flex flex-col justify-center items-center">
            {factureUrl ? (
              <iframe
                src={`${fullUrl}#toolbar=1`}
                className="w-full h-full border-none"
                title={`Facture ${factureNumero}`}
              />
            ) : (
              <div className="text-center p-8">
                <p className="text-gray-500 text-lg">Aucun document original n'est rattaché à cette facture.</p>
              </div>
            )}
          </div>

          {/* Footer (Optional) */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse shrink-0">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Fermer
            </button>
            {factureUrl && (
              <a
                  href={fullUrl}
                  download
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
              >
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
